/**
 * Playback speed for the music and videos the Hub plays in its OWN player.
 *
 * Bobbie, 2026-10-14, forwarding a staff member's text ("I suggest adding a
 * .75 and a .85 speed if that's possible?") with "Love this / Is that doable
 * to speed up videos?". She has been asked whether she also wants faster
 * speeds. Until she answers, the list is exactly 0.75, 0.85 and Normal, and
 * it is ONE list on purpose: adding a faster speed is one more number in
 * PLAYBACK_SPEEDS below and nothing else.
 *
 * Only the Hub's own <audio> and <video> elements get a speed. A YouTube,
 * Vimeo or Drive link plays inside that service's own player (an iframe from
 * another site), which the Hub cannot reach into; YouTube has its own speed
 * under its gear.
 *
 * Nothing here is saved anywhere. Every player starts at Normal each time a
 * page opens, so a slowed practice speed never carries silently into a run
 * through. The pure parts live here so they are unit tested
 * (tests/playback-speed.test.mjs); the hook and the buttons are in
 * components/PlaybackSpeedControl.tsx.
 */

/** Slowest first, Normal last. Normal (1) must stay in the list. */
export const PLAYBACK_SPEEDS: readonly number[] = [0.75, 0.85, 1];

/** The speed every player starts at, and the one the Normal button sets. */
export const NORMAL_PLAYBACK_SPEED = 1;

/**
 * Two speeds are the same speed. A float compare, because a browser hands a
 * rate back as a double (0.85 can come back as 0.8500000238418579 on some
 * engines that keep it as a float internally).
 */
export function sameSpeed(a: number, b: number): boolean {
  return Number.isFinite(a) && Number.isFinite(b) && Math.abs(a - b) < 0.001;
}

/** True for Normal (1x). */
export function isNormalSpeed(speed: number): boolean {
  return sameSpeed(speed, NORMAL_PLAYBACK_SPEED);
}

/**
 * The button and label text: "Normal" for 1x, otherwise the number with an x
 * ("0.75x"). A rate the browser's own menu picked that is not in the list
 * (Chrome's three-dot menu offers 1.5, say) reads the same way ("1.5x"), so
 * the label never lies about how fast it is really playing.
 */
export function playbackSpeedLabel(speed: number): string {
  if (isNormalSpeed(speed)) return 'Normal';
  const rounded = Math.round(speed * 100) / 100;
  return `${rounded}x`;
}

/**
 * The parts of an HTMLMediaElement this file touches, so a plain object can
 * stand in for one in a unit test. currentTime is deliberately NOT here:
 * changing speed must never move the playhead.
 */
export interface PlaybackSpeedTarget {
  playbackRate: number;
  defaultPlaybackRate: number;
  preservesPitch?: boolean;
  webkitPreservesPitch?: boolean;
  mozPreservesPitch?: boolean;
}

/**
 * Puts one element at `speed`, with its pitch kept natural.
 *
 * - preservesPitch true, plus the older webkit and moz spellings only where
 *   the element has them, so a slowed song sounds like the song, not a
 *   lower one.
 * - defaultPlaybackRate as well as playbackRate. A browser's load steps set
 *   playbackRate back to defaultPlaybackRate whenever the source loads, so
 *   setting only playbackRate would be lost on the next load.
 * - Only assigns a value that differs, so a reapply is a no-op and never
 *   fires a ratechange of its own.
 * - Each assignment is guarded: an engine that refuses a rate throws, and a
 *   speed button must never take the page down with it.
 * - Never reads or writes currentTime.
 */
export function applyPlaybackSpeed(el: PlaybackSpeedTarget, speed: number): void {
  if (!Number.isFinite(speed) || speed <= 0) return;
  try {
    if (el.preservesPitch !== true) el.preservesPitch = true;
  } catch {
    // An engine without the property: nothing to set.
  }
  if ('webkitPreservesPitch' in el) {
    try {
      if (el.webkitPreservesPitch !== true) el.webkitPreservesPitch = true;
    } catch {
      // Read only on this engine.
    }
  }
  if ('mozPreservesPitch' in el) {
    try {
      if (el.mozPreservesPitch !== true) el.mozPreservesPitch = true;
    } catch {
      // Read only on this engine.
    }
  }
  try {
    if (!sameSpeed(el.defaultPlaybackRate, speed)) el.defaultPlaybackRate = speed;
  } catch {
    // Refused by this engine; playbackRate below still carries the speed.
  }
  try {
    if (!sameSpeed(el.playbackRate, speed)) el.playbackRate = speed;
  } catch {
    // Refused by this engine; the element keeps the speed it had.
  }
}

/**
 * How many rate resets in one second the Hub undoes before it stops fighting
 * the element and shows the speed it is really playing at. An engine that
 * resets the rate once per load or once per play is undone every time; an
 * engine that snaps the rate straight back every time it is set would
 * otherwise bounce forever, so after this many in a second the control
 * follows it instead (honest, and the loop ends).
 */
export const RESETS_UNDONE_PER_SECOND = 3;

/**
 * What to do when an element reports a rate change the Hub did not ask for.
 *
 * - 'ignore': it already matches the chosen speed (our own assignment
 *   echoing back).
 * - 'reapply': a reset. Either the element has no media data yet (readyState
 *   below HAVE_METADATA, 1), which is a browser resetting the rate while it
 *   loads a source, or it has dropped playbackRate back to a literal Normal
 *   while its defaultPlaybackRate still holds the chosen speed. That second
 *   one is an engine reset, not a person: the browser's own speed menu
 *   moves BOTH rates (proven 2026-10-15 by clicking Chromium's own Playback
 *   speed menu: Normal set playbackRate and defaultPlaybackRate to 1
 *   together), and the Hub always sets both. Either way the chosen speed
 *   goes straight back on, at most RESETS_UNDONE_PER_SECOND times a second.
 * - 'follow': the element is loaded and something else changed its rate,
 *   which in practice is the browser's own speed menu (Chrome's three dots).
 *   The control follows it rather than showing Normal over a song that is
 *   really playing at 1.5x. Also what a reset becomes once it has been undone
 *   RESETS_UNDONE_PER_SECOND times in the last second.
 *
 * The one thing this cannot tell apart from an engine reset is a menu that
 * moves ONLY playbackRate, and only back to Normal: that is undone, and the
 * Normal button right under the player is the way back to Normal.
 */
export function rateChangeAction(
  elementRate: number,
  chosenSpeed: number,
  readyState: number,
  defaultRate: number,
  resetsInLastSecond = 0
): 'ignore' | 'reapply' | 'follow' {
  if (sameSpeed(elementRate, chosenSpeed)) return 'ignore';
  if (!Number.isFinite(elementRate) || elementRate <= 0) return 'reapply';
  const looksLikeReset = readyState < 1 || (isNormalSpeed(elementRate) && sameSpeed(defaultRate, chosenSpeed));
  if (looksLikeReset && resetsInLastSecond < RESETS_UNDONE_PER_SECOND) return 'reapply';
  return 'follow';
}

/**
 * Which FILE a player's url points at, so a player can tell a re-signed link
 * to the same file (keep the speed) from a different item handed to the same
 * spot on the page (start again at Normal). The query and the fragment are
 * dropped, because that is where a re-resolved link changes: a Storage
 * download token, an R2 presign's signature and expiry. The origin and the
 * path name the file itself.
 */
export function playbackItemKey(url: string): string {
  const trimmed = typeof url === 'string' ? url.trim() : '';
  try {
    const parsed = new URL(trimmed);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    // Not an absolute url: keep everything before the query or fragment.
    return trimmed.split(/[?#]/, 1)[0];
  }
}
